import { Dispatch, useImperativeHandle } from "react";
import { Accordion, Card } from "react-bootstrap";
import { get, useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";

import "./CostForm.scss";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { QuotationFormStepValidationTypes, QuotationFormStepTypes, ShipmentTypes } from "constants/";
import FormHr from "pages/shipper/RequestForm/components/Common/FormHr";
import Alert from "components/molecules/Alert/Alert";
import { IQuotationData } from "pages/provider/QuotationDetail/QuotationType";
import CostFormCharges from "./CostFormCharges";
import CostCalculationProvider from "./CostCalculationProvider";
import CostFormTotalAmount from "./CostFormTotalAmount";
import { QuotationFormState, QuotationFormAction } from "../../QuotationFormState";
import CostFormNotIncluded from "./CostFormNotIncluded";
import MultiStopTable from "./MultiStopTable";

type CostsFormProps = {
  childRef: React.MutableRefObject<any>;
  dispatch: Dispatch<QuotationFormAction>;
  state: QuotationFormState;
  quoteRequestDetails: IQuotationData;
};

export default function CostForm(props: CostsFormProps) {
  const { childRef, dispatch, state, quoteRequestDetails } = props;
  const {
    watch,
    formState: { errors },
  } = useFormContext();
  const [searchParams] = useSearchParams();
  const selectedFreightMode = watch("service.freight_mode");

  const currentActiveStep = searchParams.get("active_step");
  const costsFields = (QuotationFormStepValidationTypes[QuotationFormStepTypes.COSTS] ?? []) as string[];
  const hasError = costsFields.some((item) => get(errors, item));
  const isMultiStop = quoteRequestDetails?.organization?.shipment_type === ShipmentTypes.MULTISTOP;

  useImperativeHandle(childRef, () => ({ checkStepValidation }));

  const checkStepValidation = () => {};

  return (
    <Card className="mb-3 form-container cost-section">
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={QuotationFormStepTypes.COSTS}>
        <div className="d-flex align-items-center">
          {(hasError || state.draftSaved[QuotationFormStepTypes.COSTS]) && (
            <div className={`valid-status ${hasError ? "error" : ""}`}>{hasError ? <ExclamationIcon /> : <CheckIcon />}</div>
          )}
          <h5>
            <FormattedMessage id="general.cost" defaultMessage="Cost" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === QuotationFormStepTypes.COSTS ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={QuotationFormStepTypes.COSTS}>
        <Card.Body>
          {!selectedFreightMode ? (
            <Alert variant="warning">
              <FormattedMessage
                id="quotation-form.costs.pleaseSelectAFreightMode"
                defaultMessage="Please select a freight mode"
              />
            </Alert>
          ) : (
            <>
              {isMultiStop && (
                <MultiStopTable
                  good_items={quoteRequestDetails?.organization?.good_items || []}
                  schedule_stops={quoteRequestDetails?.schedule?.stops || []}
                  shipper={quoteRequestDetails?.organization?.shipper}
                />
              )}
              <CostCalculationProvider dispatch={dispatch}>
                <div>
                  <h6 className="mb-3">
                    <FormattedMessage id="quotation-form.costs.freightCharges" defaultMessage="Freight Charges" />
                  </h6>
                  <CostFormCharges chargeName="main_freight_charges" />
                </div>
                <FormHr />
                <div className="mt-4">
                  <h6 className="mb-3">
                    <FormattedMessage id="quotation-form.costs.otherCharges" defaultMessage="Other Charges" />
                  </h6>
                  <CostFormCharges chargeName="other_charges" />
                </div>
                <CostFormTotalAmount />
                <FormHr />
                <CostFormNotIncluded />
              </CostCalculationProvider>
            </>
          )}
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}
